import React, { useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';
import {
  Text, Button,
  Card, CardPreview, CardHeader,
  Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, } from '@fluentui/react-icons';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { useSelectedAdFormatState } from '../../../common/use-selected-ad-format';
import { STEP_PATHS } from '../../../step-constants';
import { DisplayAdPreview } from './components/display-ad-preview';
import { NativeAdPreview } from './components/native-ad-preview';
import { VideoAdPreview } from './components/video-ad-preview';
import { getConfigByType, getDescriptionByType } from './utils';
import { getAdThumbnailStyles } from './styles';

export const AdThumbnail = ({
  item,
  onDeleteAdClicked,
  type,
}) => {
  const classes = useDefaultStyles(getAdThumbnailStyles);
  const { send } = useWizardService();
  const i18n = useContext(I18nContext);
  const [, setSelectedAdFormat] = useSelectedAdFormatState();
  const ad = useMemo(() => (_.isArray(item) ? item[0] : item), [item]);
  const adId = useMemo(() => ad && ad.Id, [ad]);

  const onEditAdClick = useCallback((editingAdId) => {
    setSelectedAdFormat(type);

    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      editingAdId,
    });

    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.MULTI_NATIVE_AD,
    });
  }, [send, setSelectedAdFormat, type]);

  const onDeleteAd = useCallback((deletingAdId) => {
    onDeleteAdClicked(deletingAdId);
  }, [onDeleteAdClicked]);

  return (
    <Dialog>
      <Card className={classNames(classes.root, 'ad-thumbnail', `ad-thumbnail-${type}`)}>
        <CardPreview className={classes.cardPreview}>
          {type === AdFormat.Native && <NativeAdPreview classes={classes} ad={ad} />}
          {type === AdFormat.Display && <DisplayAdPreview ad={ad.data} />}
          {type === AdFormat.Video && <VideoAdPreview ad={ad.data} />}
        </CardPreview>
        <CardHeader
          image={getConfigByType(type).icon}
          header={<Text weight="bold">{i18n.getString(getConfigByType(type).label)}</Text>}
          description={
            <Text className={classes.previewDescription} title={getDescriptionByType(ad, type, i18n)}>
              {getDescriptionByType(ad, type, i18n)}
            </Text>
          }
          action={
            <>
              <Button onClick={() => onEditAdClick(adId)} icon={<EditRegular />} />
              <DialogTrigger disableButtonEnhancement>
                <Button icon={<DeleteRegular />} />
              </DialogTrigger>
            </>
          }
        />
      </Card>
      <DialogSurface>
        <DialogBody className="delete-dialog">
          <DialogTitle>{i18n.getString(_TL_('Delete this Ad?'))}</DialogTitle>
          <DialogContent>
            {i18n.getString(_TL_('This ad will be removed from this campaign. There is no way to undo this action.'))}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" className="delete-button" onClick={() => onDeleteAd(adId)}>{i18n.getString(_TL_('Delete Ad'))}</Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" className="cancel-button">{i18n.getString(_TL_('Cancel'))}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

AdThumbnail.propTypes = {
  item: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf({})]).isRequired,
  onDeleteAdClicked: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};
